import { ComponentConfig } from "@measured/puck";

import { GridBlock } from "./Grid";
import { GridBlockProps } from "./types";
import GridCellLayoutField, { GridCellLayoutsValue } from "src/components/Puck/config/fields/GridCellLayoutField";

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

export type { GridBlockProps } from "./types";

const generateSlotFields = (rows: number, columns: number) => {
    const fields: Record<string, { type: "slot" }> = {};

    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < columns; col++) {
            const zoneId = `grid-cell-${row}-${col}`;
            fields[zoneId] = {
                type: "slot",
            };
        }
    }

    return fields;
};

export const Grid: ComponentConfig<GridBlockProps> = {
    label: "Grid",
    fields: {
        // Generate slot fields using the same zone ID pattern for data migration
        ...generateSlotFields(4, 6),
    },
    resolveFields: (_data, { fields }) => {
        return {
            ...fields,
            cellLayouts: {
                type: "custom",
                label: "Layout",
                render: (props: unknown) => {
                    const p = isRecord(props) ? props : {};
                    const valueRaw = p["value"];
                    const value = isRecord(valueRaw) ? (valueRaw as GridCellLayoutsValue) : undefined;
                    const readOnly = Boolean(p["readOnly"]);

                    const onChangeRaw = p["onChange"];
                    const handleChange = (v: GridCellLayoutsValue) => {
                        if (typeof onChangeRaw === "function") {
                            (onChangeRaw as (next: GridCellLayoutsValue) => void)(v);
                        }
                    };

                    return <GridCellLayoutField value={value} onChange={handleChange} readOnly={readOnly} />;
                },
            },
        };
    },
    defaultProps: {
        columns: 1,
        rows: 1,
        rowGap: undefined,
        columnGap: undefined,
        backgroundColor: undefined,
        borderColor: undefined,
        borderWidth: 0,
        borderRadius: 0,
        defaultCellBackgroundColor: undefined,
        defaultCellBorderColor: undefined,
        defaultCellBorderWidth: 0,
        defaultCellBorderRadius: 0,
        gridPadding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
        },
        cellPadding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
        },
        cellLayouts: {},
        grow: false,
        columnWidth: "equal",
    },
    render: (props) => {
        return <GridBlock {...props} />;
    },
    resolveData: (data, _params) => {
        const props = isRecord(data.props) ? (data.props as Record<string, unknown>) : {};
        const cellLayouts = props["cellLayouts"];

        const nextProps: Record<string, unknown> = {
            ...(props || {}),
        };
        let changed = false;

        for (const key of Object.keys(nextProps)) {
            const match = key.match(/^grid-cell-(\d+)-(\d+)$/);
            if (!match) continue;
            if (!Array.isArray(nextProps[key])) {
                nextProps[key] = [];
                changed = true;
            }
        }

        if (!cellLayouts || typeof cellLayouts !== "object" || Array.isArray(cellLayouts)) {
            return changed ? { props: nextProps as Partial<GridBlockProps> } : {};
        }

        const nextCellLayouts: Record<string, unknown> = {};
        let changedCellLayouts = false;

        for (const [key, value] of Object.entries(cellLayouts as Record<string, unknown>)) {
            const match = key.match(/^grid-cell-(\d+)-(\d+)$/);
            if (match) {
                nextCellLayouts[`cell-${match[1]}-${match[2]}`] = value;
                changedCellLayouts = true;
                continue;
            }
            nextCellLayouts[key] = value;
        }

        if (!changed && !changedCellLayouts) {
            return {};
        }

        return {
            props: {
                ...nextProps,
                ...(changedCellLayouts && { cellLayouts: nextCellLayouts }),
            } as Partial<GridBlockProps>,
        };
    },
};
